import { useSortable } from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { Button, Switch, TableColumnType } from "antd"
import React, { useState } from "react"
import { useTranslation } from "react-i18next"
import { PurchaseOrderColumns } from "../columns/PurchaseOrderColumns"
import { DragIcon } from "../icons/DragIcon"

interface DraggableColumnItemProps {
    column: TableColumnType<any>
    isVisible: boolean
    onToggle: (key: string) => void
    disabled?: boolean
    i18nColumnBasePath: string
}

export const DraggableColumnItem = ({ column, isVisible, onToggle, disabled, i18nColumnBasePath }: DraggableColumnItemProps) => {
    const { t } = useTranslation()

    const [isToggling, setIsToggling] = useState(false)

    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
        id: column.key as string,
        disabled, // Disable sortable when disabled or when it's the name column
    })

    const style: React.CSSProperties = {
        padding: "16px 0px",
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
        background: isDragging ? "#f5f5f5" : "transparent",
        border: isDragging ? "1px dashed #ccc" : "none",
        borderRadius: isDragging ? "4px" : "0",
        cursor: disabled ? "not-allowed" : "default",
    }

    const handleToggle = (key: string) => {
        setIsToggling(true)

        // Add animation delay
        setTimeout(() => {
            onToggle(key)
            setIsToggling(false)
        }, 300)
    }

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`draggable-column-item ${isDragging ? "dragging" : ""} ${disabled ? "disabled" : ""} ${isToggling ? "column-toggling" : ""}`}
        >
            <div className={`mb-1 flex items-center justify-between gap-16 h-8 ${isDragging ? "p-1" : "p-0"}`} {...(disabled || !isVisible ? {} : { ...attributes, ...listeners })}>
                <div className="flex items-center justify-start mr-16">
                    <Button
                        type="text"
                        className={`${
                            disabled || !isVisible ? "cursor-not-allowed opacity-50" : "cursor-grab opacity-100"
                        } bg-transparent p-0 m-0 flex items-center justify-center transition-all duration-200`}
                        disabled={column.key === PurchaseOrderColumns.NAME || disabled}
                        icon={<DragIcon width={32} height={32} />}
                    />

                    <span className={`text-sm font-medium transition-all duration-200 ${disabled || !isVisible ? "text-gray-500" : "text-gray-900"}`}>
                        {t(`${i18nColumnBasePath}${column.key as string}`)}
                    </span>
                </div>

                <Switch checked={isVisible} onChange={() => handleToggle(column.key as string)} size="small" disabled={disabled} loading={isToggling} />
            </div>
        </div>
    )
}
